import { Component, computed, DestroyRef, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { take } from 'rxjs';
import dayjs from 'dayjs';
import { AbsenceRequest } from 'src/app/models/absence_request.model';
import { WorkingTimeCategory } from 'src/app/models/working_time_category.model';
import { ApiService } from 'src/app/services';
import { DatePipe } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { EmployeeRequestApprovalStatusDialogComponent } from 'src/app/components/main/shared/employee-request-approval-status-dialog/employee-request-approval-status-dialog.component';
import { MatMenu, MatMenuModule } from '@angular/material/menu';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import { UtilsService } from 'src/app/services/utils.service';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatInput } from '@angular/material/input';
import { MatDialog } from '@angular/material/dialog';
import { UploadFileConfig } from 'src/app/components/main/shared/upload-file-dialog/upload-file-dialog.component';
import { AbsenceRequestDialogComponent } from './absence-request-dialog/absence-request-dialog.component';
import { UploadFileDialogResult } from 'src/app/components/main/shared/upload-file-dialog/interfaces/UploadFileDialogResult';
import { AbsenceRequestStatusesPipe } from 'src/app/pipes/absence-request-statuses.pipe';
import { AbsenceCategoriesLegendDialogComponent } from './absence-categories-legend-dialog/absence-categories-legend-dialog.component';
import { EmployeeRequestResponsibleForAnswersRequestableTypeEnum } from 'src/app/enums/employee-request-responsible-for-answers-requestable-type.enum';
import { RequireJustificationEnum } from 'src/app/enums/require-justification.enum';
import {
  EmployeeRequestResponsibleForAnswerStatusEnum,
  EmployeeRequestResponsibleForAnswerStatusLabels,
} from 'src/app/enums/employee-request-responsible-for-answer-status.enum';
import { ClockRegisterService } from 'src/app/services/clock-register.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { UserService } from 'src/app/services/user.service';
import { EmployeeRequestApprovalStatusDialogData } from 'src/app/models/employee-request-responsible-for-answer.model';

@Component({
  selector: 'app-my-absence-requests',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatTooltip,
    MatButton,
    MatIconButton,
    RouterLink,
    DatePipe,
    MatIcon,
    MatMenu,
    MatMenuModule,
    MatPaginator,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatInput,
    MatSuffix,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    AbsenceRequestStatusesPipe,
  ],
  templateUrl: './absence-requests.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './absence-requests.component.css',
})
export class AbsenceRequestsComponent extends IndexTemplateComponent {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  displayedColumns: string[] = [];
  dataSource: MatTableDataSource<AbsenceRequestWithCustom> = new MatTableDataSource<AbsenceRequestWithCustom>();

  readonly EmployeeRequestResponsibleForAnswerStatusEnum = EmployeeRequestResponsibleForAnswerStatusEnum;
  readonly is_rrhh_view = computed((): boolean => this.router.url.includes('/rrhh/absences-management'));

  private readonly api = inject(ApiService);
  private readonly clock = inject(ClockRegisterService);
  private readonly dialog = inject(MatDialog);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly permissions = inject(PermissionsService);
  private readonly user = inject(UserService);

  protected readonly fb = inject(UntypedFormBuilder);
  protected readonly router = inject(Router);
  protected readonly activatedRoute = inject(ActivatedRoute);
  protected readonly utils = inject(UtilsService);

  protected readonly absenceRequests = signal<AbsenceRequest[]>([]);
  protected readonly clockStatus = toSignal(this.clock.statusObservable, { initialValue: false });
  protected readonly currentUserId = this.user.getCurrentUser()?.id;
  protected readonly leavesAndPermitsWorkingTimeCategories = signal<WorkingTimeCategory[]>([]);
  protected readonly is_rrhh_or_admin = toSignal(this.permissions.validateDepartmentObservable(['direction', 'rrhh']), {
    initialValue: false,
  });
  protected readonly can_create_absence_as_admin = computed((): boolean => this.is_rrhh_view() && this.is_rrhh_or_admin());

  private readonly responsibleUserIdFromQuery = this.activatedRoute.snapshot.queryParamMap.get('responsible_user_id');

  private readonly responsibleUserId =
    !this.is_rrhh_view() || this.responsibleUserIdFromQuery === ''
      ? ''
      : this.responsibleUserIdFromQuery === null
        ? (this.currentUserId ?? '')
        : Number(this.responsibleUserIdFromQuery);

  override filterFormExtraParams: Record<string, unknown> = {
    status: [
      this.activatedRoute.snapshot.queryParamMap.get('status') ??
        (this.is_rrhh_view() ? EmployeeRequestResponsibleForAnswerStatusEnum.PENDING : ''),
    ],
    responsible_user_id: [this.responsibleUserId],
  };

  constructor() {
    super(inject(Router), inject(UntypedFormBuilder), inject(ActivatedRoute), inject(UtilsService));

    if (this.is_rrhh_view() && this.responsibleUserIdFromQuery === null && this.currentUserId !== undefined) {
      void this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: {
          responsible_user_id: this.currentUserId,
        },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }

    this.setDisplayedColumns();
    this.loadWorkingTimeCategories();
  }

  protected canModifyAbsenceRequest(item: AbsenceRequest): boolean {
    if (this.can_create_absence_as_admin()) {
      return true;
    }

    if (this.isAbsenceOlderThanOneMonth(item)) {
      return false;
    }

    return item.status === EmployeeRequestResponsibleForAnswerStatusEnum.ACCEPTED;
  }

  protected canDeleteAbsenceRequest(item: AbsenceRequest): boolean {
    if (this.can_create_absence_as_admin()) {
      return true;
    }

    return !this.isAbsenceOlderThanOneMonth(item);
  }

  private isAbsenceOlderThanOneMonth(item: AbsenceRequest): boolean {
    return dayjs(item.start_date).isBefore(dayjs().subtract(1, 'month'), 'day');
  }

  private loadWorkingTimeCategories(): void {
    this.api
      .getAllAbsenceRequestWorkingTimeCategories({
        is_working_time: false,
        with: 'active_legal_workers_agreement_working_time_category',
      })
      .pipe(take(1), takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: categories => {
          this.leavesAndPermitsWorkingTimeCategories.set(categories);
          if (this.absenceRequests().length > 0) {
            this.dataSource.data = this.enrichAbsenceRequests(this.absenceRequests());
          }
        },
      });
  }

  private getAbsenceRequest$() {
    const params: Record<string, string | number | boolean> = {
      ...this.filterForm.value,
      with: this.is_rrhh_view()
        ? 'working_time_category.active_legal_workers_agreement_working_time_category,user,document,employee_request_responsible_for_answers.responsible_user'
        : 'working_time_category.active_legal_workers_agreement_working_time_category,user,document',
      order_by: 'created_at:desc',
    };
    return this.is_rrhh_view() ? this.api.getAbsenceRequests(params) : this.api.getMyAbsencesRequests(params);
  }

  override fetchData(): void {
    this.getAbsenceRequest$()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: data => {
          this.absenceRequests.set(data.data);
          this.setMetadata(data.meta.total, data.meta.current_page - 1, data.meta.per_page);
          this.dataSource.data = this.enrichAbsenceRequests(data.data);
          this.setDisplayedColumns();
        },
      });
  }

  protected goToModify(item: AbsenceRequest): void {
    void this.router.navigate([item.id, 'modify'], {
      relativeTo: this.activatedRoute.parent ?? this.activatedRoute,
      state: { absenceRequest: item },
      queryParamsHandling: 'preserve',
    });
  }

  updateAbsenceRequest(id: number): void {
    const dialogData: UploadFileConfig = {
      title: 'Añadir documentación',
      description:
        'Sube los documentos necesarios para justificar tu ausencia. Ten en cuenta que si ya subiste documentación, los nuevos archivos que añadas reemplazarán los anteriores.',
      allowedTypes: [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/jpg',
        'image/png',
      ],
      allowedExtensions: ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png'],
      maxFileSize: 10 * 1024 * 1024, // 10MB
      multiple: false,
      showComment: true,
    };

    const dialogRef = this.dialog.open(AbsenceRequestDialogComponent, {
      width: '600px',
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result: UploadFileDialogResult | undefined): void => {
      if (!result?.files.length) return;
      const formData = new FormData();
      formData.append('document', result.files[0]);
      if (result.comment) formData.append('comment', result.comment);
      this.api
        .uploadAbsenceRequestDocument(id, formData)
        .pipe(takeUntilDestroyed(this.destroy_ref))
        .subscribe({
          next: () => this.fetchData(),
        });
    });
  }

  deleteAbsenceRequest(id: number): void {
    if (!window.confirm('¿Estás seguro que quieres cancelar esta solicitud?')) return;

    this.api
      .deleteAbsenceRequest(id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: () => this.fetchData(),
      });
  }

  rejectAbsenceRequest(id: number): void {
    if (!window.confirm('Seguro que quieres rechazar esta solicitud?')) return;
    this.api
      .rejectAbsenceRequest(id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: () => this.fetchData(),
      });
  }

  approveAbsenceRequest(id: number): void {
    if (!window.confirm('Seguro que quieres aprobar esta solicitud?')) return;
    this.api
      .approveAbsenceRequest(id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: () => this.fetchData(),
      });
  }

  private setDisplayedColumns(): void {
    if (this.is_rrhh_view()) {
      this.displayedColumns = ['user', 'category', 'date_range', 'justification', 'status', 'document', 'actions'];
    } else {
      this.displayedColumns = ['category', 'date_range', 'justification', 'status', 'document', 'actions'];
    }
  }

  private enrichAbsenceRequests(absenceRequests: AbsenceRequest[]): AbsenceRequestWithCustom[] {
    return absenceRequests.map(
      (absenceRequest: AbsenceRequest): AbsenceRequestWithCustom => ({
        ...absenceRequest,
        requires_justification: this.requiresJustification(absenceRequest),
        is_all_day: this.isAllDay(absenceRequest),
        status_label: this.getStatusLabel(absenceRequest),
        status_info: this.getStatusInfo(absenceRequest),
        justification_truncated: this.getTruncatedJustification(absenceRequest.justification),
        period_starts_and_ends_at_same_day:
          absenceRequest.start_date && absenceRequest.end_date
            ? new Date(absenceRequest.start_date).toDateString() === new Date(absenceRequest.end_date).toDateString()
            : false,
      })
    );
  }

  private getStatusLabel(absenceRequest: AbsenceRequest): string {
    return EmployeeRequestResponsibleForAnswerStatusLabels[absenceRequest.status] + (absenceRequest.parent_absence_request_id ? '*' : '');
  }

  private getStatusInfo(absenceRequest: AbsenceRequest): string | null {
    return absenceRequest.parent_absence_request_id ? '(modificada)' : null;
  }

  private requiresJustification(absenceRequest: AbsenceRequest): boolean {
    return (
      absenceRequest.working_time_category?.active_legal_workers_agreement_working_time_category?.requires_justification ===
      RequireJustificationEnum.REQUIRED
    );
  }

  private isAllDay(absenceRequest: AbsenceRequest): boolean {
    const category = this.leavesAndPermitsWorkingTimeCategories().find(
      (cat: WorkingTimeCategory): boolean => cat.id === absenceRequest.working_time_category_id
    );
    return category?.active_legal_workers_agreement_working_time_category?.all_day ?? false;
  }

  private getTruncatedJustification(justification: string | null): string | null {
    if (!justification) {
      return null;
    }

    const limit: number = 50;

    return justification.length > limit ? `${justification.substring(0, limit)}...` : justification;
  }

  downloadDocument(documentId: number): void {
    this.api
      .downloadDocument(documentId)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: blob => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'document.pdf';
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
        },
      });
  }

  openCategoriesLegend(categoryId: number): void {
    this.dialog.open(AbsenceCategoriesLegendDialogComponent, {
      width: '900px',
      data: { categoryId },
    });
  }

  openApprovalStatusDialog(absence_request: AbsenceRequest): void {
    this.dialog.open(EmployeeRequestApprovalStatusDialogComponent, {
      width: '800px',
      data: {
        requestable_type: EmployeeRequestResponsibleForAnswersRequestableTypeEnum.AbsenceRequest,
        requestable_id: absence_request.id,
        requestable_status: absence_request.status,
      } as EmployeeRequestApprovalStatusDialogData,
    });
  }
}

interface AbsenceRequestWithCustom extends AbsenceRequest {
  requires_justification: boolean;
  is_all_day: boolean;
  status_label: string;
  status_info: string | null;
  justification_truncated: string | null;
  period_starts_and_ends_at_same_day: boolean;
}
